import { getImportRows } from "@/api";
import { ErrorStatusCellRenderer } from "@/components/grid";
import { Import } from "@/types/Import";
import { useQuery } from "@tanstack/react-query";
import { ColDef, GridApi } from "ag-grid-community";
import { useMemo, useState } from "react";
import { ImportError, ImportRow } from "@/types";

interface UseImportDetailsController {
  currentImport?: Import;
  importedRows?: ImportRow[];
  filteredRows?: ImportRow[];
  loading: boolean;
  columnDefs: ColDef[];
  setGridApi: (api: GridApi) => void;
  selectedRow?: ImportRow;
  setSelectedRow: (row?: ImportRow) => void;
  showAllRows: boolean;
  setShowAllRows: (show: boolean) => void;
  invalidRowCount: number;
  totalRowCount: number;
}

export const useImportDetailsController = (
  selectedImport?: Import
): UseImportDetailsController => {
  const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);
  const [selectedRow, setSelectedRow] = useState<ImportRow | undefined>(
    undefined
  );
  const [showAllRows, setShowAllRows] = useState(true);

  const toPrettyError = (error: ImportError) => {
    if (error) {
      if (error.FromColumnName && error.ToColumnName) {
        return `${error.Message} - Source: ${error.FromColumnName}, Destination: ${error.ToColumnName} on ${error.RowNumber}:${error.ColumnNumber}`;
      }
      return `${error.Message} - on ${error.RowNumber}:${error.ColumnNumber}`;
    }
    return "";
  };

  const columnDefs = useMemo<any[]>(
    () => [
      {
        headerName: "Row",
        sortable: true,
        filter: true,
        width: 100,
        flex: 0,
        valueGetter: (params: any) => {
          const value = params.data?.data?.rowNumber;
          return value || "N/A";
        },
      },
      {
        headerName: "Entity Type",
        width: 150,
        flex: 0,
        sortable: true,
        filter: true,
        valueGetter: (params: any) => {
          const value = params.data?.data?.entityType;
          return value || "N/A";
        },
        valueFormatter: (params: any) => {
          const value = params.value;
          if (value && value !== "N/A") {
            return value.charAt(0).toUpperCase() + value.slice(1);
          }
          return "N/A";
        },
      },
      {
        field: "hasError",
        headerName: "Status",
        width: 150,
        flex: 0,
        sortable: true,
        filter: true,
        cellRenderer: ErrorStatusCellRenderer,
      },
      {
        headerName: "Error Details",
        sortable: true,
        filter: true,
        width: 400,
        minWidth: 300,
        maxWidth: 600,
        resizable: true,
        wrapText: true,
        autoHeight: false,
        cellStyle: {
          whiteSpace: 'normal',
          lineHeight: '1.2',
          paddingTop: '4px',
          paddingBottom: '4px',
        },
        valueGetter: (params: any) => {
          const error = params.data?.data?.error;
          if (!error) return "";
          try {
            const parsed = JSON.parse(error);
            if (Array.isArray(parsed) && parsed.length > 0) {
              return toPrettyError(parsed[0]);
            }
            return error;
          } catch {
            return error;
          }
        },
      },
    ],
    []
  );

  const result = useQuery({
    queryKey: [
      "import-details",
      selectedImport?.importId ? String(selectedImport.importId) : "",
      selectedImport?.status,
    ],
    queryFn: async () => {
      if (!selectedImport?.importId) {
        throw new Error("No import ID provided");
      }

      console.log(
        "Loading import rows for import ID:",
        selectedImport.importId
      );

      try {
        const rows = await getImportRows(String(selectedImport.importId));
        console.log("Received import rows:", rows);

        if (!rows || !Array.isArray(rows)) {
          console.error("Import rows data is not an array:", rows);
          return [];
        }

        const formattedRows: ImportRow[] = rows.map(
          (row: any, index: number) => {
            // Check if this is already in the expected format
            if (
              row.id &&
              row.importId &&
              row.data &&
              typeof row.hasError === "boolean"
            ) {
              return row as ImportRow;
            }

            // Map the API response structure to ImportRow format
            const hasError = !!(row.error && row.error !== null && row.error !== "" && row.error !== "null");
            
            const formattedRow: ImportRow = {
              id: row.importRowId ? String(row.importRowId) : `row-${index + 1}`,
              importId: String(selectedImport.importId),
              rowNumber: row.rowNumber || index + 1,
              data: {
                ...row,
                source: row.source,
                entityType: row.entityType,
                entityId: row.entityId,
                error: row.error, // Preserve the original error for SourceViewer
              },
              hasError: hasError,
              errorMessage: row.error || undefined,
            };

            return formattedRow;
          }
        );

        console.log("Formatted rows:", formattedRows);
        return formattedRows;
      } catch (error) {
        console.error("Error fetching import rows:", error);
        throw error;
      }
    },
    enabled: selectedImport?.importId !== undefined && 
             (selectedImport?.status === "Finished" || 
              selectedImport?.status === "FinishedWithErrors"),
  });

  const importedRows = result.data || [];
  
  // Filter rows based on showAllRows state
  const filteredRows = useMemo(() => {
    if (showAllRows) {
      return importedRows;
    }
    return importedRows.filter(row => row.hasError);
  }, [importedRows, showAllRows]);

  const invalidRowCount = useMemo(() => {
    return importedRows.filter(row => row.hasError).length;
  }, [importedRows]);

  const totalRowCount = importedRows.length;

  return {
    currentImport: selectedImport,
    importedRows,
    filteredRows,
    loading: result.isLoading || result.isFetching,
    columnDefs,
    setGridApi,
    selectedRow,
    setSelectedRow,
    showAllRows,
    setShowAllRows,
    invalidRowCount,
    totalRowCount,
  };
};
